/* © CNES */

import { QueryFunctionContext } from '@tanstack/query-core';
import { NavigateFunction } from 'react-router-dom';
import { Asset } from '../../models/CollectionDetails';
import { apiConfiguration } from '../APIConfiguration';
import { authenticationAPI } from '../authentication/AuthenticateAPI';
import { handleHttpError } from '../common';
import responseMockCartItems from '../mock/response_cart_items.json';

interface SaveSelectedProductsParams {
  payload: Array<{
    user: string;
    name: string;
    storage?: string;
    downloaded?: string;
    mission?: string;
    urn?: string;
    quicklook?: string;
    checksum?: string;
    endpointURL: string;
    href: string;
    assets: Asset[];
  }>;
}

/**
 * Store selected product into db.
 * @param queryKey the query parameters.
 * @returns a Promise that will return a 200 status if the request has successfully completed
 */
export const saveSelectedProducts = async ({
  queryKey,
}: QueryFunctionContext<
  [string, SaveSelectedProductsParams, NavigateFunction, Function]
>): Promise<any> => {
  let res: Response | undefined;
  const [, params, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/context/product/createWithList `,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
        body: JSON.stringify(params.payload),
      }
    );
    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify({ test: 'ADDED' }));
  }
  return res.json();
};

/**
 * Get the stored product for user.
 * @param queryKey the query parameters.
 * @returns a Promise that will return a 200 status if the request has successfully completed
 */
export const getSavedProducts = async ({
  queryKey,
}: QueryFunctionContext<any>): Promise<any> => {
  let res: Response | undefined;
  const [, params] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/context/product/findForUser?user=${params.user}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );
  } else {
    res = new Response(JSON.stringify(responseMockCartItems));
  }
  return res.json();
};

/**
 * Delete selected product from db.
 * @param queryKey the query parameters.
 * @returns a Promise that will return a 200 status if the request has successfully completed
 */
export const deleteSavedProduct = async ({
  queryKey,
}: QueryFunctionContext<any>): Promise<any> => {
  let res: Response | undefined;
  const [, params] = queryKey;

  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/context/product/deleteSelectedProducts?user=${params.payload[0].user}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
        body: JSON.stringify(params.payload),
      }
    );
  } else {
    res = new Response(JSON.stringify({ test: 'DELETED' }));
  }
  return res?.json();
};

interface RestoreArchivedProductParams {
  payload: {
    product: string;
    process: string;
  };
}

/**
 * launch restoration product from tier3
 * @param queryKey product name to restore
 * @returns
 */
export const restoreArchivedProduct = async ({
  queryKey,
}: QueryFunctionContext<
  [string, RestoreArchivedProductParams]
>): Promise<any> => {
  const [, params] = queryKey;

  let res: Response | undefined;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(`${apiConfiguration.apiBaseUrl}/restoration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authenticationAPI.getAuthorizationHeaders(),
      },
      body: JSON.stringify(params.payload),
    });
  } else {
    res = new Response(JSON.stringify({ test: 'restore process' }));
  }
  return res;
};

export interface UpdateProductParams {
  productId: string;
  payload: {
    user: string;
    name: string;
    storage: string;
    downloaded: string;
    mission: string;
    urn: string;
    quicklook: string;
  };
}

/**
 * Update Product.
 * @param queryKey the query parameters.
 * @returns a Promise that will return a 200 status if the request has successfully completed
 */
export const updateProduct = async ({
  queryKey,
}: QueryFunctionContext<[string, UpdateProductParams]>): Promise<any> => {
  let res: Response | undefined;
  const [, params] = queryKey;

  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/context/product/update/${params.productId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
        body: JSON.stringify(params.payload),
      }
    );
  } else {
    res = new Response(JSON.stringify('Success'));
  }
  return res.json();
};
